import { useState, useEffect } from 'react'
import { AdminLayout } from '../../components/layouts/Layout'
import { DataTable } from '../../components/common/DataTable'
import { Card, Button, Badge, Modal, Textarea, Select, Input } from '../../components/common/UI'
import { AlertCircle, Eye, FileText, HelpCircle, Clock } from 'lucide-react'
import { formatters } from '../../utils/formatting'
import { reportService } from '../../services/api'
import { useAdminAuth } from '../../hooks/useAdminAuth'
import type { EnrichedReport, ReportThreadEntry } from '../../types'

export default function Incidents() {
  const { admin } = useAdminAuth()
  const [reports, setReports] = useState<EnrichedReport[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedReport, setSelectedReport] = useState<any>(null)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [showActionModal, setShowActionModal] = useState(false)
  const [currentAction, setCurrentAction] = useState('')
  const [actionData, setActionData] = useState<any>({})
  const [filterTab, setFilterTab] = useState<'ALL' | 'REPORT' | 'HELP'>('ALL')
  const [filterStatus, setFilterStatus] = useState<string>('ALL')
  const [filterPriority, setFilterPriority] = useState<string>('ALL')

  useEffect(() => {
    loadReports()
  }, [filterTab, filterStatus, filterPriority])

  const loadReports = async () => {
    try {
      setLoading(true)
      const filters: any = {}
      
      if (filterTab !== 'ALL') {
        filters.reportType = filterTab
      }
      if (filterStatus !== 'ALL') {
        filters.status = filterStatus
      }
      if (filterPriority !== 'ALL') {
        filters.priority = filterPriority
      }

      const result = await reportService.list(filters)
      setReports(result.reports || [])
    } catch (error) {
      console.error('Failed to load reports:', error)
    } finally {
      setLoading(false)
    }
  }

  const viewDetails = async (reportId: string) => {
    try {
      const details = await reportService.get(reportId)
      setSelectedReport(details)
      setShowDetailsModal(true)
    } catch (error) {
      console.error('Failed to load report details:', error)
    }
  }

  const handleAssignToMe = async () => {
    if (!selectedReport || !admin) return
    try {
      await reportService.assignToMe(selectedReport.report.id, admin.id)
      await viewDetails(selectedReport.report.id)
    } catch (error) {
      console.error('Failed to assign report:', error)
    }
  }

  const handleAddNote = async () => {
    if (!selectedReport || !admin || !actionData.notes) return
    try {
      await reportService.addNote(selectedReport.report.id, admin.id, actionData.notes)
      setActionData({})
      await viewDetails(selectedReport.report.id)
    } catch (error) {
      console.error('Failed to add note:', error)
    }
  }

  const handleChangeStatus = async () => {
    if (!selectedReport || !admin || !actionData.newStatus) return
    try {
      await reportService.changeStatus(selectedReport.report.id, admin.id, actionData.newStatus)
      setActionData({})
      setShowActionModal(false)
      await viewDetails(selectedReport.report.id)
    } catch (error) {
      console.error('Failed to change status:', error)
    }
  }

  const handleChangePriority = async () => {
    if (!selectedReport || !admin || !actionData.newPriority) return
    try {
      await reportService.changePriority(selectedReport.report.id, admin.id, actionData.newPriority)
      setActionData({})
      setShowActionModal(false)
      await viewDetails(selectedReport.report.id)
    } catch (error) {
      console.error('Failed to change priority:', error)
    }
  }

  const handleWarnUser = async () => {
    if (!selectedReport || !admin || !actionData.warningMessage) return
    try {
      await reportService.warnUser(selectedReport.report.id, admin.id, actionData.warningMessage)
      setActionData({})
      setShowActionModal(false)
      await viewDetails(selectedReport.report.id)
    } catch (error) {
      console.error('Failed to warn user:', error)
    }
  }

  const handleSuspendUser = async () => {
    if (!selectedReport || !admin || !actionData.suspensionDays || !actionData.reason) return
    try {
      await reportService.suspendUser(
        selectedReport.report.id, 
        admin.id, 
        parseInt(actionData.suspensionDays), 
        actionData.reason
      )
      setActionData({})
      setShowActionModal(false)
      await viewDetails(selectedReport.report.id)
    } catch (error) {
      console.error('Failed to suspend user:', error)
    }
  }

  const handleBanUser = async () => {
    if (!selectedReport || !admin || !actionData.reason) return
    try {
      await reportService.banUser(selectedReport.report.id, admin.id, actionData.reason)
      setActionData({})
      setShowActionModal(false)
      await viewDetails(selectedReport.report.id)
    } catch (error) {
      console.error('Failed to ban user:', error)
    }
  }

  const handleRemoveContent = async () => {
    if (!selectedReport || !admin) return
    try {
      await reportService.removeContent(selectedReport.report.id, admin.id)
      setShowActionModal(false)
      await viewDetails(selectedReport.report.id)
    } catch (error) {
      console.error('Failed to remove content:', error)
    }
  }

  const handleDismiss = async () => {
    if (!selectedReport || !admin || !actionData.reason) return
    try {
      await reportService.dismiss(selectedReport.report.id, admin.id, actionData.reason)
      setActionData({})
      setShowActionModal(false)
      setShowDetailsModal(false)
      await loadReports()
    } catch (error) {
      console.error('Failed to dismiss report:', error)
    }
  }

  const handleResolve = async () => {
    if (!selectedReport || !admin || !actionData.resolutionAction || !actionData.resolutionNotes) return
    try {
      await reportService.resolve(
        selectedReport.report.id, 
        admin.id, 
        actionData.resolutionAction, 
        actionData.resolutionNotes
      )
      setActionData({})
      setShowActionModal(false)
      setShowDetailsModal(false)
      await loadReports()
    } catch (error) {
      console.error('Failed to resolve report:', error)
    }
  }

  const openActionModal = (action: string) => {
    setCurrentAction(action)
    setActionData({})
    setShowActionModal(true)
  }

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'OPEN': return 'warning'
      case 'IN_PROGRESS': return 'default'
      case 'RESOLVED': return 'success'
      case 'DISMISSED': return 'default'
      default: return 'default'
    }
  }

  const getPriorityBadgeVariant = (priority: string) => {
    switch (priority) {
      case 'CRITICAL': return 'error'
      case 'HIGH': return 'warning'
      case 'MEDIUM': return 'default'
      case 'LOW': return 'default'
      default: return 'default'
    }
  }

  const getTypeBadgeVariant = (type: string) => {
    return type === 'REPORT' ? 'error' : 'default'
  }

  const columns = [
    {
      key: 'id' as const,
      label: 'Incident ID',
      render: (value: string) => (
        <span className="font-mono text-xs">{value.slice(0, 8)}</span>
      )
    },
    {
      key: 'reportType' as const,
      label: 'Type',
      render: (value: string) => (
        <Badge variant={getTypeBadgeVariant(value)}>
          {value === 'REPORT' ? <FileText className="w-3 h-3 mr-1" /> : <HelpCircle className="w-3 h-3 mr-1" />}
          {value}
        </Badge>
      )
    },
    {
      key: 'reporterInfo' as const,
      label: 'From User',
      render: (value: any) => (
        <div>
          <p className="font-medium">{value?.fullName || 'Unknown'}</p>
          <p className="text-xs text-gray-500">{value?.email || ''}</p>
        </div>
      )
    },
    {
      key: 'subject' as const,
      label: 'Subject',
      render: (value: string) => (
        <span className="max-w-xs truncate block">{value}</span>
      )
    },
    {
      key: 'priority' as const,
      label: 'Priority',
      render: (value: string) => (
        <Badge variant={getPriorityBadgeVariant(value)}>{value}</Badge>
      )
    },
    {
      key: 'status' as const,
      label: 'Status',
      render: (value: string) => (
        <Badge variant={getStatusBadgeVariant(value)}>{value}</Badge>
      )
    },
    {
      key: 'assignedAdminInfo' as const,
      label: 'Assigned To',
      render: (value: any) => (
        value ? `${value.firstName} ${value.lastName}` : '-'
      )
    },
    {
      key: 'createdAt' as const,
      label: 'Created',
      render: (value: string) => formatters.date(value)
    }
  ]

  const actions = [
    {
      icon: Eye,
      label: 'View Details',
      onClick: (row: EnrichedReport) => viewDetails(row.id)
    }
  ]

  const stats = {
    total: reports.length,
    open: reports.filter(r => r.status === 'OPEN').length,
    inProgress: reports.filter(r => r.status === 'IN_PROGRESS').length,
    critical: reports.filter(r => r.priority === 'CRITICAL').length
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full"></div>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Incidents Management</h1>
            <p className="text-gray-600">Manage user reports and help requests</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Incidents</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-blue-600" />
            </div>
          </Card>
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Open</p>
                <p className="text-2xl font-bold">{stats.open}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>
          </Card>
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">In Progress</p>
                <p className="text-2xl font-bold">{stats.inProgress}</p>
              </div>
              <FileText className="w-8 h-8 text-purple-600" />
            </div>
          </Card>
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Critical Priority</p>
                <p className="text-2xl font-bold">{stats.critical}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
          </Card>
        </div>

        <Card>
          <div className="flex space-x-2 mb-6 border-b">
            {['ALL', 'REPORT', 'HELP'].map((tab) => (
              <button
                key={tab}
                onClick={() => setFilterTab(tab as any)}
                className={`px-4 py-2 font-medium ${
                  filterTab === tab
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab === 'ALL' ? 'All Incidents' : tab === 'REPORT' ? 'Reports' : 'Help Requests'}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-2">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border border-gray-300 rounded-lg px-4 py-2"
              >
                <option value="ALL">All Status</option>
                <option value="OPEN">Open</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="RESOLVED">Resolved</option>
                <option value="DISMISSED">Dismissed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Priority</label>
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="border border-gray-300 rounded-lg px-4 py-2"
              >
                <option value="ALL">All Priority</option>
                <option value="CRITICAL">Critical</option>
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </select>
            </div>
          </div>

          <DataTable 
            data={reports} 
            columns={columns} 
            actions={actions}
          />
        </Card>

        <Modal
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false)
            setSelectedReport(null)
          }}
          title="Incident Details"
          size="xl"
        >
          {selectedReport && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Incident Information</h3>
                    <div className="space-y-2">
                      <div>
                        <p className="text-sm text-gray-600">Incident ID</p>
                        <p className="font-mono text-sm">{selectedReport.report.id}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Type</p>
                        <Badge variant={getTypeBadgeVariant(selectedReport.report.reportType)}>
                          {selectedReport.report.reportType}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Status</p>
                        <Badge variant={getStatusBadgeVariant(selectedReport.report.status)}>
                          {selectedReport.report.status}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Priority</p>
                        <Badge variant={getPriorityBadgeVariant(selectedReport.report.priority)}>
                          {selectedReport.report.priority}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Created</p>
                        <p className="font-medium">{formatters.date(selectedReport.report.createdAt)}</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-4">Reporter</h3>
                    <div className="space-y-2">
                      <div>
                        <p className="text-sm text-gray-600">Name</p>
                        <p className="font-medium">{selectedReport.reporterDetails?.fullName}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Email</p>
                        <p className="font-medium">{selectedReport.reporterDetails?.email}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Username</p>
                        <p className="font-medium">@{selectedReport.reporterDetails?.username}</p>
                      </div>
                    </div>
                  </div>

                  {selectedReport.report.reportType === 'REPORT' && selectedReport.reportedUserDetails && (
                    <div>
                      <h3 className="text-lg font-semibold mb-4">Reported User</h3>
                      <div className="space-y-2">
                        <div>
                          <p className="text-sm text-gray-600">Name</p>
                          <p className="font-medium">{selectedReport.reportedUserDetails.fullName}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Email</p>
                          <p className="font-medium">{selectedReport.reportedUserDetails.email}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <h3 className="text-lg font-semibold mb-2">Subject</h3>
                    <p>{selectedReport.report.subject}</p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-2">Message</h3>
                    <p className="whitespace-pre-wrap">{selectedReport.report.message}</p>
                  </div>

                  {selectedReport.report.reportType === 'REPORT' && (
                    <div>
                      <h3 className="text-lg font-semibold mb-2">Report Details</h3>
                      <div className="space-y-2">
                        <div>
                          <p className="text-sm text-gray-600">Reason</p>
                          <Badge>{selectedReport.report.reportReason}</Badge>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Content Type</p>
                          <Badge>{selectedReport.report.reportedItemType}</Badge>
                        </div>
                        {selectedReport.reportedContentDetails && (
                          <div>
                            <p className="text-sm text-gray-600">Content Still Exists</p>
                            <Badge variant="success">Yes</Badge>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {selectedReport.stats && selectedReport.stats.totalReportsAgainstUser > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold mb-2">Related Reports</h3>
                      <p className="text-sm">
                        Total reports against this user: <span className="font-bold">{selectedReport.stats.totalReportsAgainstUser}</span>
                      </p>
                      <p className="text-sm">
                        Active reports: <span className="font-bold">{selectedReport.stats.activeReportsAgainstUser}</span>
                      </p>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Thread History</h3>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {selectedReport.report.threadParsed && selectedReport.report.threadParsed.map((entry: ReportThreadEntry, _index: number) => (
                        <div key={entry.actionId} className="border-l-2 border-gray-300 pl-4 pb-3">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="text-xs text-gray-500">
                              {formatters.date(entry.timestamp)}
                            </span>
                            <Badge variant="default">{entry.actionType}</Badge>
                          </div>
                          {entry.adminName && (
                            <p className="text-sm font-medium text-gray-700">By: {entry.adminName}</p>
                          )}
                          <p className="text-sm text-gray-600 mt-1">{entry.notes}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
                    <div className="space-y-2">
                      {!selectedReport.report.assignedToAdminId && (
                        <Button onClick={handleAssignToMe} className="w-full">
                          Assign to Me
                        </Button>
                      )}
                      
                      <div className="space-y-2">
                        <Textarea
                          placeholder="Add a note..."
                          value={actionData.notes || ''}
                          onChange={(e) => setActionData({ ...actionData, notes: e.target.value })}
                          rows={3}
                        />
                        <Button onClick={handleAddNote} variant="outline" className="w-full" disabled={!actionData.notes}>
                          Add Note
                        </Button>
                      </div>

                      <Button onClick={() => openActionModal('CHANGE_STATUS')} variant="outline" className="w-full">
                        Change Status
                      </Button>
                      <Button onClick={() => openActionModal('CHANGE_PRIORITY')} variant="outline" className="w-full">
                        Change Priority
                      </Button>

                      {selectedReport.report.reportType === 'REPORT' && (
                        <>
                          <Button onClick={() => openActionModal('WARN_USER')} variant="outline" className="w-full">
                            Issue Warning
                          </Button>
                          <Button onClick={() => openActionModal('SUSPEND_USER')} variant="outline" className="w-full">
                            Suspend User
                          </Button>
                          <Button onClick={() => openActionModal('BAN_USER')} variant="ghost" className="w-full text-red-600">
                            Ban User
                          </Button>
                          {selectedReport.reportedContentDetails && (
                            <Button onClick={() => openActionModal('REMOVE_CONTENT')} variant="ghost" className="w-full text-red-600">
                              Remove Content
                            </Button>
                          )}
                        </>
                      )}

                      <Button onClick={() => openActionModal('DISMISS')} variant="ghost" className="w-full">
                        Dismiss Incident
                      </Button>
                      <Button onClick={() => openActionModal('RESOLVE')} className="w-full bg-green-600 hover:bg-green-700">
                        Resolve Incident
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal>

        <Modal
          isOpen={showActionModal}
          onClose={() => {
            setShowActionModal(false)
            setActionData({})
          }}
          title={`${currentAction.replace(/_/g, ' ')}`}
        >
          <div className="space-y-4">
            {currentAction === 'CHANGE_STATUS' && (
              <>
                <Select
                  label="New Status"
                  value={actionData.newStatus || ''}
                  onChange={(e) => setActionData({ ...actionData, newStatus: e.target.value })}
                  options={[
                    { value: '', label: 'Select status' },
                    { value: 'OPEN', label: 'Open' },
                    { value: 'IN_PROGRESS', label: 'In Progress' },
                    { value: 'RESOLVED', label: 'Resolved' },
                    { value: 'DISMISSED', label: 'Dismissed' }
                  ]}
                />
                <Button onClick={handleChangeStatus} className="w-full" disabled={!actionData.newStatus}>
                  Change Status
                </Button>
              </>
            )}

            {currentAction === 'CHANGE_PRIORITY' && (
              <>
                <Select
                  label="New Priority"
                  value={actionData.newPriority || ''}
                  onChange={(e) => setActionData({ ...actionData, newPriority: e.target.value })}
                  options={[
                    { value: '', label: 'Select priority' },
                    { value: 'LOW', label: 'Low' },
                    { value: 'MEDIUM', label: 'Medium' },
                    { value: 'HIGH', label: 'High' },
                    { value: 'CRITICAL', label: 'Critical' }
                  ]}
                />
                <Button onClick={handleChangePriority} className="w-full" disabled={!actionData.newPriority}>
                  Change Priority
                </Button>
              </>
            )}

            {currentAction === 'WARN_USER' && (
              <>
                <Textarea
                  label="Warning Message"
                  value={actionData.warningMessage || ''}
                  onChange={(e) => setActionData({ ...actionData, warningMessage: e.target.value })}
                  rows={4}
                  placeholder="Enter warning message to send to user..."
                />
                <Button onClick={handleWarnUser} className="w-full" disabled={!actionData.warningMessage}>
                  Send Warning
                </Button>
              </>
            )}

            {currentAction === 'SUSPEND_USER' && (
              <>
                <Input
                  label="Suspension Duration (days)"
                  type="number"
                  value={actionData.suspensionDays || ''}
                  onChange={(e) => setActionData({ ...actionData, suspensionDays: e.target.value })}
                  placeholder="7"
                />
                <Textarea
                  label="Suspension Reason"
                  value={actionData.reason || ''}
                  onChange={(e) => setActionData({ ...actionData, reason: e.target.value })}
                  rows={4}
                  placeholder="Enter suspension reason..."
                />
                <Button onClick={handleSuspendUser} className="w-full" disabled={!actionData.suspensionDays || !actionData.reason}>
                  Suspend User
                </Button>
              </>
            )}

            {currentAction === 'BAN_USER' && (
              <>
                <Textarea
                  label="Ban Reason"
                  value={actionData.reason || ''}
                  onChange={(e) => setActionData({ ...actionData, reason: e.target.value })}
                  rows={4}
                  placeholder="Enter permanent ban reason..."
                />
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">
                    <strong>Warning:</strong> This will permanently ban the user. This action cannot be undone.
                  </p>
                </div>
                <Button onClick={handleBanUser} className="w-full bg-red-600 hover:bg-red-700" disabled={!actionData.reason}>
                  Permanently Ban User
                </Button>
              </>
            )}

            {currentAction === 'REMOVE_CONTENT' && (
              <>
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Warning:</strong> This will permanently delete the reported content. This action cannot be undone.
                  </p>
                </div>
                <Button onClick={handleRemoveContent} className="w-full bg-red-600 hover:bg-red-700">
                  Remove Content
                </Button>
              </>
            )}

            {currentAction === 'DISMISS' && (
              <>
                <Textarea
                  label="Dismissal Reason"
                  value={actionData.reason || ''}
                  onChange={(e) => setActionData({ ...actionData, reason: e.target.value })}
                  rows={4}
                  placeholder="Enter reason for dismissing this incident..."
                />
                <Button onClick={handleDismiss} className="w-full" disabled={!actionData.reason}>
                  Dismiss Incident
                </Button>
              </>
            )}

            {currentAction === 'RESOLVE' && (
              <>
                <Select
                  label="Resolution Action"
                  value={actionData.resolutionAction || ''}
                  onChange={(e) => setActionData({ ...actionData, resolutionAction: e.target.value })}
                  options={[
                    { value: '', label: 'Select action' },
                    { value: 'NO_ACTION', label: 'No Action Needed' },
                    { value: 'WARNING_SENT', label: 'Warning Sent' },
                    { value: 'CONTENT_REMOVED', label: 'Content Removed' },
                    { value: 'USER_SUSPENDED', label: 'User Suspended' },
                    { value: 'USER_BANNED', label: 'User Banned' },
                    { value: 'HELP_PROVIDED', label: 'Help Provided' }
                  ]}
                />
                <Textarea
                  label="Resolution Notes"
                  value={actionData.resolutionNotes || ''}
                  onChange={(e) => setActionData({ ...actionData, resolutionNotes: e.target.value })}
                  rows={4}
                  placeholder="Enter resolution notes..."
                />
                <Button onClick={handleResolve} className="w-full bg-green-600 hover:bg-green-700" disabled={!actionData.resolutionAction || !actionData.resolutionNotes}>
                  Resolve Incident
                </Button>
              </>
            )}
          </div>
        </Modal>
      </div>
    </AdminLayout>
  )
}